import React from "react";
import { graphql } from "react-apollo";
import { compose } from "recompose";
import { Link } from "react-router-dom";
import { withStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import withWidth from "@material-ui/core/withWidth";
import { sharedStyles } from "../components/EntryScreenMenu";
import Heading3 from "../styleguide/Heading3";
import withErrorState from "../hocs/withErrorState";
import withLoadingState from "../hocs/withLoadingState";
import withLocalization from "../hocs/withLocalization";
import withFlattenedTranslations from "../hocs/withFlattenedTranslations";
import withConnectionStatus from "../hocs/withConnectionStatus";
import entryScreenQuery from "../queries/entryScreen";
import teaserPlaceholder from "../assets/images/home/navigation_stadtbusse_placeholder.png";
import Button from "@material-ui/core/Button";
import EntryScreenPodcasts from "../components/EntryScreenPodcasts";
import classnames from "classnames";
import buildAssetUrl from "../utils/buildAssetUrl";
import withGeneralTranslations from "../hocs/withGeneralTranslations";

const styles = (theme) => ({
    ...sharedStyles(),
    containerStyle: {
        flex: "1 0 auto",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: 1200,
        width: "100%",
        paddingTop: 32,
        paddingBottom: 32,
    },
    entryScreenItemStyle: {
        width: "100%",
        height: 591,
        backgroundSize: "cover",
        backgroundPosition: "top center",
        backgroundRepeat: "no-repeat",
        boxShadow: "0 0 4px 0 #d6d8db",
        padding: 24,
    },
    entryScreenItemStyleHalf: {
        height: "calc(591px / 2)",

        "&:nth-child(2)": {
            height: "calc(591px / 2 - 24px)",
            marginTop: 24,
        },
    },
    entryScreenItemTitleStyle: {
        fontSize: 40,
        color: "#fff",
        textTransform: "uppercase",
        marginBottom: 16,
    },
    entryScreenItemBtnStyle: {
        backgroundColor: "#fff",
        color: "#303C49",
        textTransform: "none",
        marginBottom: 8,
        boxShadow: "none",
    },
});

const EntryScreenItem = compose(
    withGeneralTranslations,
    withStyles(styles)
)(
    ({
        title,
        linkUrl,
        image,
        subItems,
        half,
        generalTranslations,
        classes: { entryScreenItemStyle, entryScreenItemStyleHalf, entryScreenItemTitleStyle, entryScreenItemBtnStyle },
    }) => {
        let imageUrl = teaserPlaceholder;
        if (image) {
            const { url } = buildAssetUrl(image);
            imageUrl = url;
        }

        return (
            <div
                className={classnames(entryScreenItemStyle, { [entryScreenItemStyleHalf]: half })}
                style={{ backgroundImage: `url(${imageUrl})` }}
            >
                <Heading3 bold className={entryScreenItemTitleStyle}>
                    <span>{title}</span>
                </Heading3>
                {linkUrl && (
                    <React.Fragment>
                        {linkUrl.startsWith("http") ? (
                            <Button
                                component="a"
                                href={linkUrl}
                                target="_blank"
                                variant="contained"
                                className={entryScreenItemBtnStyle}
                            >
                                {generalTranslations("more")}
                            </Button>
                        ) : (
                            <Button
                                component={Link}
                                to={linkUrl}
                                variant="contained"
                                className={entryScreenItemBtnStyle}
                            >
                                {generalTranslations("more")}
                            </Button>
                        )}
                    </React.Fragment>
                )}
                {subItems.map((subItem) => (
                    <div key={subItem.id}>
                        <Button
                            component={Link}
                            to={subItem.linkUrl}
                            variant="contained"
                            className={entryScreenItemBtnStyle}
                        >
                            {subItem.title}
                        </Button>
                    </div>
                ))}
            </div>
        );
    }
);

const EntryScreen = ({ entryScreenItems, classes: { containerStyle } }) => {
    const tcoTool = entryScreenItems.find((item) => item.key === "tcotool");
    const lionsCity = entryScreenItems.find((item) => item.key === "lionscity");
    const lionsIntercity = entryScreenItems.find((item) => item.key === "lionsintercity");
    const transportSolutions = entryScreenItems.find((item) => item.key === "transportsolutions");
    const eMobility = entryScreenItems.find((item) => item.key === "emobility");
    const myBus = entryScreenItems.find((item) => item.key === "mybus");
    const buscast = entryScreenItems.find((item) => item.key === "buscast");

    return (
        <div className={containerStyle}>
            <div>
                <Grid container spacing={24}>
                    <Grid item xs={4}>
                        {lionsCity && (
                            <EntryScreenItem
                                title={lionsCity.title}
                                linkUrl={lionsCity.linkUrl}
                                image={lionsCity.teaserImage}
                                subItems={lionsCity.entryScreenSubItems}
                                half={Boolean(lionsIntercity)}
                            />
                        )}
                        {lionsIntercity && (
                            <EntryScreenItem
                                title={lionsIntercity.title}
                                linkUrl={lionsIntercity.linkUrl}
                                image={lionsIntercity.teaserImage}
                                subItems={lionsIntercity.entryScreenSubItems}
                                half
                            />
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        {tcoTool && (
                            <EntryScreenItem
                                title={tcoTool.title}
                                linkUrl={tcoTool.linkUrl}
                                image={tcoTool.teaserImage}
                                subItems={tcoTool.entryScreenSubItems}
                                half
                            />
                        )}
                        {myBus && (
                            <EntryScreenItem
                                title={myBus.title}
                                linkUrl={myBus.linkUrl}
                                image={myBus.teaserImage}
                                subItems={myBus.entryScreenSubItems}
                                half
                            />
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        {transportSolutions && (
                            <EntryScreenItem
                                title={transportSolutions.title}
                                linkUrl={transportSolutions.linkUrl}
                                image={transportSolutions.teaserImage}
                                subItems={transportSolutions.entryScreenSubItems}
                                half
                            />
                        )}
                        {eMobility && (
                            <EntryScreenItem
                                title={eMobility.title}
                                linkUrl={eMobility.linkUrl}
                                image={eMobility.teaserImage}
                                subItems={eMobility.entryScreenSubItems}
                                half
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <EntryScreenPodcasts entryScreen={buscast} />
                    </Grid>
                </Grid>
            </div>
        </div>
    );
};

export default compose(
    withConnectionStatus,
    withLocalization,
    graphql(entryScreenQuery, {
        options: ({ language, fetchPolicy, stage }) => ({
            variables: {
                lang: language.toLowerCase(),
                stage,
            },
            fetchPolicy,
        }),
    }),
    withErrorState,
    withLoadingState("entryScreenItems"),
    withFlattenedTranslations("entryScreenItems"),
    withWidth(),
    withStyles(styles)
)(EntryScreen);



// WEBPACK FOOTER //
// ./src/screens/EntryScreen.js